import { Controller } from "@hotwired/stimulus";
import { volBoughtValidationStatus } from "../scenario_modeling/vol_bought_validation";
import {
  calculateVolDeclaredTotal,
  toggleVolDeclaredEmptyState,
} from "../scenario_modeling/vol_declared_input";

export default class extends Controller {
  static targets = [
    "taxesLeviesInput",
    "otherCostsInput",
    "volDeclaredInput",
    "warningsSection",
    "warningItem",
  ];

  static values = {
    panelId: String,
  };

  connect() {
    this._onWarningsExpand = () => this.syncVolumeMismatchWarning();
    window.addEventListener("scenario-warnings:expand", this._onWarningsExpand);
    this.syncVolumeFeedback();
  }

  disconnect() {
    window.removeEventListener("scenario-warnings:expand", this._onWarningsExpand);
  }

  validateVolDeclared(event) {
    toggleVolDeclaredEmptyState(event.target);
    this.previewVolDeclared();
  }

  previewVolDeclared() {
    const volDeclared = this._currentVolDeclared();

    window.dispatchEvent(
      new CustomEvent("scenario-modeling:vol-declared-change", {
        detail: {
          panelId: this.panelIdValue,
          volDeclared,
        },
      }),
    );

    this.syncVolumeFeedback();
  }

  previewCosts() {
    const taxesLevies = this._parseInputValue(this.taxesLeviesInputTarget);
    const otherCosts = this._parseInputValue(this.otherCostsInputTarget);

    window.dispatchEvent(
      new CustomEvent("scenario-modeling:costs-change", {
        detail: {
          panelId: this.panelIdValue,
          taxesLevies,
          otherCosts,
        },
      }),
    );
  }

  syncVolumeFeedback() {
    this.syncVolumeMismatchWarning();
  }

  syncVolumeMismatchWarning() {
    if (!this.hasWarningItemTarget) return;

    const showWarning = this._showsVolumeMismatchWarning();

    this.warningItemTargets.forEach((item) => {
      if (item.dataset.warningType !== "volume_mismatch") return;
      this._setWarningItemHidden(item, !showWarning);
    });

    if (!this.hasWarningsSectionTarget) return;

    const anyVisible = this.warningItemTargets.some((item) => !item.classList.contains("hidden"));
    this.warningsSectionTarget.classList.toggle("hidden", !anyVisible);
  }

  _setWarningItemHidden(item, hidden) {
    if (hidden || item.dataset.warningsCollapsed === "true") {
      item.classList.add("hidden");
      return;
    }

    item.classList.remove("hidden");
  }

  // Mirrors intermediary_scenario_warnings: hide when match/neutral.
  _showsVolumeMismatchWarning() {
    const status = this._volumeValidationStatus();
    return status !== "match" && status !== "neutral";
  }

  _volumeValidationStatus() {
    const cell = this.element.querySelector("[data-scenario-modeling-summary-target='volBoughtCell']");
    if (!cell) return "neutral";

    const volBought = Number(cell.dataset.volBought) || 0;
    return volBoughtValidationStatus(volBought, this._currentVolDeclared());
  }

  // Inputs are edited in each facility's own (producer) unit; data-to-total-factor
  // converts them into the summary totals unit. Inputs without a finite factor are
  // in a non-convertible unit and are excluded, matching the server-side total.
  // Keep the exact converted sum so tonne decimals (e.g. 181.4) are not integer-rounded.
  _currentVolDeclared() {
    if (this.volDeclaredInputTargets.length > 0) {
      const declaredEl = this.element.querySelector("[data-scenario-modeling-summary-target='volDeclared']");
      return calculateVolDeclaredTotal(
        declaredEl?.dataset.volDeclaredExact,
        this.volDeclaredInputTargets.map((input) => ({
          currentValue: input.value,
          initialValue: input.defaultValue,
          factor: input.dataset.toTotalFactor ?? "1",
        })),
      );
    }

    const declaredEl = this.element.querySelector("[data-scenario-modeling-summary-target='volDeclared']");
    return Number((declaredEl?.textContent || "").replace(/,/g, "")) || 0;
  }

  _parseInputValue(input) {
    const value = Number.parseFloat(input.value);
    return Number.isFinite(value) ? value : 0;
  }
}
